import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { createSignal, For, Show } from "solid-js";
import { MapPin, Warehouse as WarehouseIcon } from "@fleet/icons";
import {
  createLocationMutation,
  createWarehouseMutation,
  getLocationsQuery,
  getWarehousesQuery,
  queryKeys,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import { appRoute } from "./route";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { PageContainer, PageHeader } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { EmptyWarehouseIllustration } from "@/components/ui/illustrations";
import { Select } from "@/components/ui/select";
import type { LocationRecord, WarehouseRecord } from "@stockpilot/powersync";

function WarehousesPage() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();

  const [whName, setWhName] = createSignal("");
  let whNameInput: HTMLInputElement | undefined;
  const [locName, setLocName] = createSignal("");
  const [locWarehouse, setLocWarehouse] = createSignal("");

  const warehouses = createPowerSyncQuery<WarehouseRecord>({
    queryKey: () => queryKeys.inventory.warehouses(orgId()),
    queryFn: () => getWarehousesQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled: () => Boolean(drizzle() && orgId()),
  });

  async function handleCreateWarehouse(e: Event) {
    e.preventDefault();
    const value = whName().trim();
    const d = drizzle();
    if (!value || !d) {
      return;
    }
    await createWarehouseMutation(d, orgId(), value);
    setWhName("");
  }

  async function handleCreateLocation(e: Event) {
    e.preventDefault();
    const value = locName().trim();
    const d = drizzle();
    if (!value || !d) {
      return;
    }
    await createLocationMutation(d, orgId(), {
      name: value,
      warehouseId: locWarehouse() || null,
    });
    setLocName("");
  }

  return (
    <PageContainer>
      <PageHeader
        title={t(msg`Warehouses`)}
        description={t(msg`Warehouses and storage locations (bins).`)}
        icon={WarehouseIcon}
      />

      <form class="mt-6 flex gap-2" onSubmit={handleCreateWarehouse} data-testid="warehouse-form">
        <input
          class="flex-1 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
          placeholder={t(msg`Warehouse name`)}
          value={whName()}
          ref={whNameInput}
          onInput={(e) => setWhName(e.currentTarget.value)}
          data-testid="warehouse-name-input"
        />
        <Button type="submit" disabled={!drizzle()} data-testid="warehouse-add-button">
          {t(msg`Add warehouse`)}
        </Button>
      </form>

      <div class="mt-4" data-testid="warehouse-list">
        <Show
          when={(warehouses.data ?? []).length > 0}
          fallback={
            <EmptyState
              icon={WarehouseIcon}
              illustration={EmptyWarehouseIllustration}
              title={t(msg`No warehouses yet`)}
              description={t(msg`Add a warehouse to organize your locations.`)}
              onIconActivate={() => whNameInput?.focus()}
              iconActionLabel={t(msg`Add your first warehouse`)}
            />
          }
        >
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={warehouses.data ?? []}>
              {(w) => (
                <li class="flex items-center gap-3 px-4 py-3" data-testid="warehouse-row">
                  <WarehouseIcon class="h-5 w-5 text-primary shrink-0" />
                  <span class="font-medium">{w.name}</span>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>

      <h2 class="mt-8 text-lg font-semibold">{t(msg`Locations`)}</h2>
      <form class="mt-3 flex gap-2" onSubmit={handleCreateLocation} data-testid="location-form">
        <input
          class="flex-1 h-10 rounded-md border border-border bg-field-background px-3 text-sm"
          placeholder={t(msg`Location name`)}
          value={locName()}
          onInput={(e) => setLocName(e.currentTarget.value)}
          data-testid="location-name-input"
        />
        <Select
          class="w-44"
          value={locWarehouse()}
          onChange={(v) => setLocWarehouse(v)}
          data-testid="location-warehouse-select"
          aria-label={t(msg`Warehouse`)}
          options={[
            { value: "", label: t(msg`No warehouse`) },
            ...(warehouses.data ?? []).map((w) => ({ value: w.id, label: w.name })),
          ]}
        />
        <Button type="submit" disabled={!drizzle()} data-testid="location-add-button">
          {t(msg`Add location`)}
        </Button>
      </form>

      <div class="mt-4" data-testid="location-list">
        <Show when={(locations.data ?? []).length > 0}>
          <ul class="divide-y divide-border rounded-md border border-border">
            <For each={locations.data ?? []}>
              {(l) => (
                <li class="flex items-center gap-3 px-4 py-3" data-testid="location-row">
                  <MapPin class="h-5 w-5 text-primary shrink-0" />
                  <span class="font-medium">{l.name}</span>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </div>
    </PageContainer>
  );
}

export const warehousesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warehouses",
  component: WarehousesPage,
});
